import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { login } from "./api";
import MarketingSlider from "./MarketingSlider";
import "./Login.css";
import "./Policy.css";

export default function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState(null);

  // Provide cloud image from public/ via CSS variable to avoid css-loader resolution
  // Set immediately to avoid flash of dark page on initial load
  useEffect(() => {
    document.documentElement.style.setProperty("--clouds-url", "url(/clouds-dark.jpg)");
    document.body.classList.add("login-page");
    return () => {
      document.documentElement.style.removeProperty("--clouds-url");
      document.body.classList.remove("login-page");
    };
  }, []);

  // Keep arc peak aligned with the card title across resizes
  useEffect(() => {
    const updateArc = () => {
      const title = document.querySelector('.login-title');
      const card = document.querySelector('.login-card');
      if (!title) return;
      const rect = title.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2; // px from viewport top
      // nudge a bit downward so the arc peak sits right behind the title
      const y = Math.round(centerY + 2);
      document.documentElement.style.setProperty('--arc-y', `${y}px`);
      // Also set minimum arc width based on login card width so arrow never narrower than card
      if (card) {
        const w = Math.round(card.getBoundingClientRect().width);
        document.documentElement.style.setProperty('--card-width', `${w}px`);
      }
    };
    // Run immediately and on next frame to catch layout shifts
    updateArc();
    requestAnimationFrame(updateArc);
    window.addEventListener('resize', updateArc);
    return () => window.removeEventListener('resize', updateArc);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { token, user } = await login(email.trim(), password);
      onSuccess({ token, user, remember });
    } catch (err) {
      console.error("Login error:", err);
      setError(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotStatus(null);
    try {
      const api = await import('./api');
      await api.forgotPassword(forgotEmail.trim());
      setForgotStatus({ ok: true, message: 'If that email exists, a reset link was sent.' });
    } catch (err) {
      console.error('Forgot error', err);
      setForgotStatus({ ok: false, message: err?.message || 'Could not submit request' });
    }
  };

  return (
    <>
      <div className="login-split">
        <section className="login-pane login-pane--auth">
        <div className="auth-sticky">
      {/* Brand arc sweeping up behind the logo/title (desktop split only).
          Same art as the original login arc, cropped to its bounding box. */}
      <svg className="auth-arrow" viewBox="210 120 1580 480" fill="none" aria-hidden="true">
        <defs>
          {/* Horizontal fade: transparent at the tail (left) -> bright at the
             arrowhead (right). Both ends sit on the same y (540) so the arc is
             symmetric about its center. */}
          <linearGradient id="authArcGrad" x1="260" y1="540" x2="1740" y2="540" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="18%" stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95" />
          </linearGradient>
          <marker id="authArcHead" markerUnits="strokeWidth" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0 0 L6 3 L0 6 Z" fill="#ffffff" />
          </marker>
        </defs>
        <path
          d="M260 540 A 900 900 0 0 1 1740 540"
          stroke="url(#authArcGrad)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd="url(#authArcHead)"
        />
      </svg>
      {/* Centered logo above login card */}
      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>
      {/* Page/system title above login card; keeps class for arc alignment */}
      <h2 className="login-title page-title">Performance Dashboard</h2>

      <div className="login-container">
        <div className="login-card">
          <form className="login-form" onSubmit={handleSubmit}>
          {!showForgot ? (
          <>
          <div className="input-with-icon">
            <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              className="login-input"
              type="email"
              placeholder="Email address"
              aria-label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="input-with-icon">
            <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 11V8a5 5 0 0110 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              className="login-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              aria-label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className={`input-icon-right input-toggle`}
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {/* Show plain eye when password is hidden, and eye-with-slash when visible */}
              {showPassword ? (
                /* password is visible -> show eye + slash */
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 26 26" fill="none" aria-hidden>
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                /* password is hidden -> show plain eye */
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 26 26" fill="none" aria-hidden>
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              )}
            </button>
          </div>

          <div className="options-row">
            <label className="remember-row">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="remember-text">Remember me</span>
            </label>

            <button type="button" onClick={() => { setForgotEmail(email); setShowForgot(true); }} className="link-button">Forgot password?</button>
          </div>

          {error && <div className="login-error">{error}</div>}

          <button className="login-button" type="submit" disabled={loading}>
            {loading ? "Logging in…" : "Login"}
          </button>
          </>
          ) : (
            <div className="forgot-container">
              <h3 className="forgot-title">Reset your password</h3>
              <p className="forgot-description">
                Enter your user account's verified email address and we will send you a password reset link.
              </p>
              
              <div className="forgot-row input-with-icon">
                <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <label className="visually-hidden">Email address</label>
                <input
                  className="login-input"
                  type="email"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
              </div>

              <div className="forgot-actions">
                <button className="login-button" type="button" onClick={handleForgot}>Send reset link</button>
                <button type="button" className="link-button" onClick={() => { setShowForgot(false); setForgotStatus(null); }}>Back</button>
              </div>

              {forgotStatus && <div className="forgot-status" role="status" aria-live="polite">{forgotStatus.message}</div>}
            </div>
          )}
        </form>
      </div>
      <div className="login-request-account" style={{ marginTop: "16px", textAlign: "center" }}>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", marginRight: "8px" }}>
          Don't have an account?
        </span>
        <Link
          to="/request-account"
          style={{
            display: "inline-block",
            padding: "9px 18px",
            border: "1px solid rgba(255,122,0,0.6)",
            borderRadius: "8px",
            color: "#ff9a4a",
            textDecoration: "none",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Request an account
        </Link>
      </div>
      </div>
      <div className="login-policy-footer">
        <Link to="/terms">Terms of Service</Link>
        <span className="footer-sep">·</span>
        <Link to="/privacy">Privacy Policy</Link>
        <span className="footer-sep">·</span>
        <Link to="/account-deletion">Account Deletion</Link>
      </div>
        </div>
        </section>
        <aside className="login-pane login-pane--marketing">
          <MarketingSlider />
        </aside>
      </div>
    </>
  );
}
